import { Card } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import type { EstimateViewItem } from "@shared-core/data/api-types";
import {
  contributionPercent,
  estimateStatusLabel,
  estimateStatusTone,
} from "@shared-core/domain/estimate-presentation";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import {
  DRAFT_ESTIMATE_STATE_LABELS,
  draftEstimateStateTone,
} from "@shared-core/domain/merchandise-presentation";
import { Building2 } from "lucide-react";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";

interface EstimateCardProps {
  estimate: EstimateViewItem;
  onClick?: () => void;
}

export function EstimateCard({ estimate, onClick }: EstimateCardProps) {
  const marginPct = contributionPercent(
    estimate.estimate_amt,
    estimate.contribution_amt,
  );
  // A row not yet replicated has no Tharstern estimate number (finding 85's
  // fact-table lag) — fall back to the internal id rather than dead-end.
  const detailId = estimate.estimate_id || estimate.id;
  // A merchandise draft carries its own state (null on every other row). It
  // replaces the open/won/lost badge, and an unpriced draft says so rather
  // than reading as a zero-pound estimate (spec 06, EstimatesPage).
  const draftState = estimate.draft_state ?? null;
  const awaitingQuote = draftState === "awaiting_quote";

  return (
    <Card
      className="p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Link
              to={paths.estimateDetail({ id: detailId })}
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-[length:var(--text-base)] hover:underline text-[color:var(--color-primary)]"
            >
              {estimate.estimate_id}
            </Link>
            {draftState ? (
              <Badge variant={draftEstimateStateTone(draftState)}>
                {DRAFT_ESTIMATE_STATE_LABELS[draftState]}
              </Badge>
            ) : (
              <Badge variant={estimateStatusTone(estimate.status)}>
                {estimateStatusLabel(estimate.status)}
              </Badge>
            )}
          </div>

          {estimate.customer && (
            <div className="mb-2 flex items-center gap-1.5 text-sm">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <Link
                to={paths.customerDetail({ customerId: estimate.customer.id })}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-[color:var(--color-primary)] hover:underline truncate"
              >
                {estimate.customer.name}
              </Link>
            </div>
          )}

          <p className="text-sm text-muted-foreground mb-3 break-words">
            {estimate.description}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm text-muted-foreground [&>div]:min-w-0">
            <div>
              <span className="block text-xs">Estimate Amount</span>
              <span className="block font-medium text-foreground truncate">
                {awaitingQuote
                  ? "Awaiting quote"
                  : `£${estimate.estimate_amt.toLocaleString("en-GB", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
              </span>
            </div>
            <div>
              <span className="block text-xs">Contribution</span>
              <span className="block font-medium text-foreground truncate">
                {awaitingQuote ? (
                  "—"
                ) : (
                  <>
                    £
                    {estimate.contribution_amt.toLocaleString("en-GB", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{" "}
                    <span className="text-xs text-muted-foreground">
                      {marginPct == null ? "(—)" : `(${marginPct.toFixed(1)}%)`}
                    </span>
                  </>
                )}
              </span>
            </div>
            <div>
              <span className="block text-xs">Date</span>
              <span className="block font-medium text-foreground truncate">
                {formatDateOrDash(estimate.estimate_date)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
